import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Toast from "../Toast";
import Swal from "sweetalert2";

const API = "/backend/public";

const fileFields = [
  { name: "photo", label: "Applicant Photo", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 70KB" },
  { name: "signature", label: "Signature", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 50KB" },
  { name: "voter_card", label: "Voter Card", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "pan_card", label: "PAN Card", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "trade_license", label: "Trade License", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "inside_photo", label: "Inside Centre Photo", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "outside_photo", label: "Outside Centre Photo", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "theory_room", label: "Theory Room", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
  { name: "practical_room", label: "Practical Room", accept: ".jpg,.jpeg,image/jpeg", hint: "Format: .jpg/.jpeg | Max: 100KB" },
];

const requiredFields = {
  state_id: "State is required",
  district_id: "District is required",
  city: "City is required",
  academy_category: "Academy category is required",
  institute_name: "Institute name is required",
  address: "Address is required",
  pincode: "Pincode is required",
  contact: "Contact number is required",
  email: "Institute email is required",
  incharge_name: "Incharge name is required",
  designation: "Designation is required",
  gender: "Gender is required",
  dob: "Date of birth is required",
  incharge_mobile: "Incharge mobile is required",
  incharge_email: "Incharge email is required",
};

const initialForm = {
  state_id: "",
  district_id: "",
  city: "",
  academy_category: "",
  institute_name: "",
  address: "",
  pincode: "",
  contact: "",
  email: "",
  incharge_name: "",
  designation: "",
  gender: "",
  dob: "",
  incharge_mobile: "",
  incharge_email: "",
};

const requiredFileMessages = fileFields.reduce((acc, field) => {
  acc[field.name] = `${field.label} is required`;
  return acc;
}, {});

function FranchiseApplicationForm({ mode = "public" }) {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({});
  const [preview, setPreview] = useState({});
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [academyCategories, setAcademyCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [toast, setToast] = useState(null);
  const [lastApplicationNo, setLastApplicationNo] = useState("");

  useEffect(() => {
    let active = true;

    const loadInitialData = async () => {
      try {
        const [stateRes, districtRes, academyRes] = await Promise.all([
          axios.get(`${API}/api/states`),
          axios.get(`${API}/api/districts`),
          axios.get(`${API}/api/academy-categories`),
        ]);

        if (!active) {
          return;
        }

        setStates(stateRes.data || []);
        setDistricts(districtRes.data || []);
        setAcademyCategories(academyRes.data || []);
      } catch (error) {
        if (!active) {
          return;
        }

        setToast({
          type: "error",
          title: "Load failed",
          message: "Form setup data could not be loaded. Please refresh once.",
        });
      } finally {
        if (active) {
          setBooting(false);
        }
      }
    };

    loadInitialData();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setToast(null);
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [toast]);

  const filteredDistricts = useMemo(() => {
    return districts.filter(
      (district) => String(district.state_id) === String(form.state_id || "")
    );
  }, [districts, form.state_id]);

  const completion = useMemo(() => {
    const requiredCount = Object.keys(requiredFields).length;
    const filledCount = Object.keys(requiredFields).filter(
      (field) => String(form[field] || "").trim() !== ""
    ).length;

    return Math.round((filledCount / requiredCount) * 100);
  }, [form]);

  const selectedState = states.find(
    (item) => String(item.id) === String(form.state_id || "")
  );
  const selectedDistrict = filteredDistricts.find(
    (item) => String(item.id) === String(form.district_id || "")
  );

  const resetForm = () => {
    setForm(initialForm);
    setFiles({});
    setPreview({});
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};

    Object.entries(requiredFields).forEach(([field, message]) => {
      if (!String(form[field] || "").trim()) {
        nextErrors[field] = message;
      }
    });

    Object.entries(requiredFileMessages).forEach(([field, message]) => {
      if (!files[field]) {
        nextErrors[field] = message;
      }
    });

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid institute email";
    }

    if (
      form.incharge_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.incharge_email)
    ) {
      nextErrors.incharge_email = "Enter a valid incharge email";
    }

    if (form.contact && !/^[0-9]{10,15}$/.test(form.contact)) {
      nextErrors.contact = "Contact number should be 10 to 15 digits";
    }

    if (form.incharge_mobile && !/^[0-9]{10,15}$/.test(form.incharge_mobile)) {
      nextErrors.incharge_mobile = "Incharge mobile should be 10 to 15 digits";
    }

    if (form.pincode && !/^[0-9]{5,10}$/.test(form.pincode)) {
      nextErrors.pincode = "Pincode should be 5 to 10 digits";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "state_id" ? { district_id: "" } : {}),
    }));

    setErrors((current) => {
      if (!current[name] && !(name === "state_id" && current.district_id)) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[name];
      if (name === "state_id") {
        delete nextErrors.district_id;
      }
      return nextErrors;
    });
  };

  const handleFile = (event) => {
    const file = event.target.files[0];
    const { name } = event.target;

    if (!file) {
      return;
    }

    setFiles((current) => ({
      ...current,
      [name]: file,
    }));

    if (file.type.startsWith("image/")) {
      setPreview((current) => ({
        ...current,
        [name]: URL.createObjectURL(file),
      }));
    }
  };

  const submit = async () => {
    if (!validateForm()) {
      setToast({
        type: "error",
        title: "Please review the form",
        message: "Required fields or formatting still need attention.",
      });
      return;
    }

    try {
      setLoading(true);

      const data = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          data.append(key, value);
        }
      });
      data.append("application_source", mode === "admin" ? "admin" : "public");

      Object.entries(files).forEach(([key, value]) => {
        data.append(key, value);
      });

      const response = await axios.post(`${API}/api/franchise-apply`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (mode === "public" && response.data?.application_no) {
        await Swal.fire({
          icon: "success",
          title: "Application Submitted",
          html: `
            <div style="line-height:1.7">
              <div>${response.data?.message || "Franchise application has been saved successfully."}</div>
              <div style="margin-top:12px;font-size:16px">Application No</div>
              <div style="margin-top:6px;font-size:22px;font-weight:800;color:#3730a3">${response.data.application_no}</div>
              <div style="margin-top:10px;font-size:13px;color:#64748b">Please keep this number to check your application status later.</div>
            </div>
          `,
          confirmButtonText: "OK",
          confirmButtonColor: "#7c2d3a",
          allowOutsideClick: false,
        });
      } else {
        setToast({
          type: "success",
          title: "Application submitted",
          message: response.data?.message || "Franchise application has been saved successfully.",
        });
      }
      setLastApplicationNo(response.data?.application_no || "");
      resetForm();
    } catch (error) {
      const apiErrors = error.response?.data?.errors || {};
      const flatErrors = {};

      Object.keys(apiErrors).forEach((field) => {
        flatErrors[field] = Array.isArray(apiErrors[field])
          ? apiErrors[field][0]
          : apiErrors[field];
      });

      if (Object.keys(flatErrors).length) {
        setErrors(flatErrors);
      }

      setToast({
        type: "error",
        title: "Submission failed",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "The application could not be saved right now.",
      });
    } finally {
      setLoading(false);
    }
  };

  const cardStyle = {
    border: "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: "24px",
    background: "rgba(255, 255, 255, 0.94)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
  };

  const sectionTitleStyle = {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#0f172a",
    marginBottom: "16px",
  };

  const renderField = (label, input, fieldName) => (
    <div className="col-md-6">
      <label className="form-label fw-semibold text-dark">{label} <span className="text-danger">*</span></label>
      {input}
      {errors[fieldName] ? (
        <div className="text-danger small mt-1">{errors[fieldName]}</div>
      ) : null}
    </div>
  );

  return (
    <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
      <Toast
        message={toast?.message}
        title={toast?.title}
        type={toast?.type}
        onClose={() => setToast(null)}
      />

      <div
        style={{
          ...cardStyle,
          overflow: "hidden",
          marginBottom: "24px",
          background:
            "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 50%, #c2410c 100%)",
          color: "#f8fafc",
        }}
      >
        <div className="row g-0 align-items-stretch">
          <div className="col-lg-8 p-4 p-lg-5">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "999px",
                background: "rgba(255,255,255,0.12)",
                padding: "6px 12px",
                fontSize: "13px",
                marginBottom: "16px",
              }}
            >
              {mode === "admin" ? "Admin entry panel" : "Public application desk"}
            </div>
            <h2 className="fw-bold mb-3">Franchise Application Form</h2>
            <p className="mb-0" style={{ maxWidth: "720px", color: "#cbd5e1" }}>
              Kickstart your journey with NIDCS through our streamlined Franchise Application Form. This all-in-one guided portal allows you to easily submit academy categories, location details, center profiles, and supporting documents. Complete your registration in one go and take the first step toward building your own successful educational hub
            </p>
          </div>

          <div className="col-lg-4 p-4 p-lg-5">
            <div
              style={{
                borderRadius: "22px",
                background: "rgba(255,255,255,0.08)",
                padding: "20px",
                height: "100%",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small text-uppercase" style={{ letterSpacing: "0.08em" }}>
                  Completion
                </span>
                <span className="fw-semibold">{completion}%</span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "10px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.14)",
                  overflow: "hidden",
                  marginBottom: "18px",
                }}
              >
                <div
                  style={{
                    width: `${completion}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #f59e0b, #fde68a)",
                  }}
                />
              </div>
              <div className="small" style={{ color: "#e2e8f0", lineHeight: 1.8 }}>
                <div>Academy: {form.academy_category || "Not selected yet"}</div>
                <div>State: {selectedState?.name || "Pending"}</div>
                <div>District: {selectedDistrict?.name || "Pending"}</div>
                <div>Documents attached: {Object.keys(files).length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lastApplicationNo ? (
        <div className="alert alert-success mb-4" style={{ borderRadius: "18px", border: "1px solid #86efac" }}>
          <div className="fw-bold">Application submitted successfully.</div>
          <div>Your Application No: <strong>{lastApplicationNo}</strong></div>
          <div className="small mt-1">Please keep this number to check application status later.</div>
        </div>
      ) : null}

      <div className="row g-4">
        <div className="col-12">
          <div style={cardStyle} className="p-4 p-lg-5">
            <div style={sectionTitleStyle}>Location and Academy Mapping</div>
            <div className="row g-3">
              {renderField(
                "State",
                <select
                  name="state_id"
                  className="form-select form-select-lg"
                  value={form.state_id}
                  onChange={handleChange}
                  disabled={booting}
                  required
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>,
                "state_id"
              )}

              {renderField(
                "District",
                <select
                  name="district_id"
                  className="form-select form-select-lg"
                  value={form.district_id}
                  onChange={handleChange}
                  disabled={booting || !form.state_id}
                  required
                >
                  <option value="">Select District</option>
                  {filteredDistricts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>,
                "district_id"
              )}

              {renderField(
                "City",
                <input
                  type="text"
                  name="city"
                  className="form-control form-control-lg"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="Enter city name"
                  required
                />,
                "city"
              )}

              {renderField(
                "Applying For",
                <select
                  name="academy_category"
                  className="form-select form-select-lg"
                  value={form.academy_category}
                  onChange={handleChange}
                  disabled={booting}
                  required
                >
                  <option value="">Select Academy Category</option>
                  {academyCategories.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>,
                "academy_category"
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div style={cardStyle} className="p-4 p-lg-5 h-100">
            <div style={sectionTitleStyle}>Institute Information</div>
            <div className="row g-3">
              {renderField(
                "Institute Name",
                <input
                  type="text"
                  name="institute_name"
                  className="form-control form-control-lg"
                  value={form.institute_name}
                  onChange={handleChange}
                  placeholder="Enter institute name"
                  required
                />,
                "institute_name"
              )}

              {renderField(
                "Pincode",
                <input
                  type="text"
                  name="pincode"
                  className="form-control form-control-lg"
                  value={form.pincode}
                  onChange={handleChange}
                  placeholder="Enter pincode"
                  required
                />,
                "pincode"
              )}

              <div className="col-12">
                <label className="form-label fw-semibold text-dark">Full Address <span className="text-danger">*</span></label>
                <textarea
                  name="address"
                  className="form-control"
                  value={form.address}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Write full institute address"
                  required
                />
                {errors.address ? (
                  <div className="text-danger small mt-1">{errors.address}</div>
                ) : null}
              </div>

              {renderField(
                "Contact Number",
                <input
                  type="text"
                  name="contact"
                  className="form-control form-control-lg"
                  value={form.contact}
                  onChange={handleChange}
                  placeholder="Primary institute contact"
                  required
                />,
                "contact"
              )}

              {renderField(
                "Institute Email",
                <input
                  type="email"
                  name="email"
                  className="form-control form-control-lg"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Institute email address"
                  required
                />,
                "email"
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div style={cardStyle} className="p-4 p-lg-5 h-100">
            <div style={sectionTitleStyle}>Centre Incharge</div>
            <div className="row g-3">
              {renderField(
                "Incharge Name",
                <input
                  type="text"
                  name="incharge_name"
                  className="form-control form-control-lg"
                  value={form.incharge_name}
                  onChange={handleChange}
                  placeholder="Enter incharge name"
                  required
                />,
                "incharge_name"
              )}

              {renderField(
                "Designation",
                <input
                  type="text"
                  name="designation"
                  className="form-control form-control-lg"
                  value={form.designation}
                  onChange={handleChange}
                  placeholder="Centre head, owner, manager"
                  required
                />,
                "designation"
              )}

              {renderField(
                "Gender",
                <select
                  name="gender"
                  className="form-select form-select-lg"
                  value={form.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>,
                "gender"
              )}

              {renderField(
                "Date of Birth",
                <input
                  type="date"
                  name="dob"
                  className="form-control form-control-lg"
                  value={form.dob}
                  onChange={handleChange}
                  required
                />,
                "dob"
              )}

              {renderField(
                "Mobile",
                <input
                  type="text"
                  name="incharge_mobile"
                  className="form-control form-control-lg"
                  value={form.incharge_mobile}
                  onChange={handleChange}
                  placeholder="Incharge mobile number"
                  required
                />,
                "incharge_mobile"
              )}

              {renderField(
                "Incharge Email",
                <input
                  type="email"
                  name="incharge_email"
                  className="form-control form-control-lg"
                  value={form.incharge_email}
                  onChange={handleChange}
                  placeholder="Incharge email"
                  required
                />,
                "incharge_email"
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div style={cardStyle} className="p-4 p-lg-5">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div style={sectionTitleStyle} className="mb-0">
                Document Desk
              </div>
              <div className="small text-secondary">
                Attach files step by step. Image files show instant preview.
              </div>
            </div>

            <div className="row g-3">
              {fileFields.map((field) => (
                <div className="col-md-6 col-xl-4" key={field.name}>
                  <div
                    style={{
                      border: "1px dashed rgba(100, 116, 139, 0.45)",
                      borderRadius: "18px",
                      padding: "16px",
                      height: "100%",
                      background: "#f8fafc",
                    }}
                  >
                    <label className="form-label fw-semibold text-dark">{field.label} <span className="text-danger">*</span></label>
                    <input
                      type="file"
                      name={field.name}
                      accept={field.accept}
                      onChange={handleFile}
                      className="form-control"
                      required
                    />
                    <div className="small text-secondary mt-2">
                      {files[field.name]?.name || "No file selected"}
                    </div>
                    <div className="small text-muted mt-1">{field.hint}</div>
                    {errors[field.name] ? (
                      <div className="text-danger small mt-1">{errors[field.name]}</div>
                    ) : null}
                    {preview[field.name] ? (
                      <img
                        src={preview[field.name]}
                        alt={field.label}
                        className="img-fluid rounded mt-3"
                        style={{ maxHeight: "120px", objectFit: "cover", width: "100%" }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div
            style={{
              ...cardStyle,
              padding: "24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div>
              <div className="fw-bold text-dark">
                {mode === "admin"
                  ? "Admin can create franchise applications directly from here."
                  : "Submit once all required details and documents are ready."}
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary btn-lg"
                onClick={resetForm}
                disabled={loading}
              >
                Reset Form
              </button>
              <button
                type="button"
                className={`btn btn-lg ${mode === "admin" ? "btn-success" : "btn-primary"}`}
                onClick={submit}
                disabled={loading || booting}
              >
                {loading
                  ? "Saving application..."
                  : mode === "admin"
                  ? "Save Application"
                  : "Submit Application"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FranchiseApplicationForm;



